import { SorterResult } from 'antd/es/table/interface';
import { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import {
  AdminInsurerAttention,
  AdminInsurerAttentionFilterParams,
} from '@/types/admin-insurer-attention';
import { useAdminInsurerAttentionBulkDelete } from '@/hooks/admin';
import useAppToast from '@/hooks/useAppToast';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBreadcrumb, AppButton, AppContainer, AppPagination } from '../atoms';
import { Plus } from '../atoms/AppIcon';
import { InsurerAttentionTable } from '../molecules';
import { AdminInsurerAttentionFilter, AdminInsurerAttentionReflectBox } from '../organisms';

interface AdminInsurerAttentionListTemplateProps {
  data: AdminInsurerAttention[];
  current: number;
  total: number;
  pageSize: number;
  sortInfo?: {
    columnKey?: string;
    order?: 'ascend' | 'descend';
  };
  onChange: (page: number) => void;
  onTableChange?: (
    sorter: SorterResult<AdminInsurerAttention> | SorterResult<AdminInsurerAttention>[]
  ) => void;
  onSearch?: (filters: AdminInsurerAttentionFilterParams) => void;
  onBulkCopySuccess?: () => void;
  onDeleteSuccess?: () => void;
  breadcrumbItems: BreadcrumbItemType[];
  isLoading?: boolean;
  reflectRecord: AdminInsurerAttention | null;
  onReflect: (record: AdminInsurerAttention) => void;
}

const AdminInsurerAttentionListTemplate = ({
  data,
  isLoading,
  current,
  total,
  pageSize,
  sortInfo,
  onChange,
  onTableChange,
  onSearch,
  onBulkCopySuccess,
  onDeleteSuccess,
  breadcrumbItems,
  reflectRecord,
  onReflect,
}: AdminInsurerAttentionListTemplateProps) => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const openDeleteConfirm = useStandardDeleteConfirm();

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const reflectBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reflectRecord) return;
    const el = reflectBoxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offset = 80;
    window.scrollBy({ top: rect.top - offset, behavior: 'smooth' });
  }, [reflectRecord]);

  const { mutate: bulkDelete, isPending: isDeleting } = useAdminInsurerAttentionBulkDelete(() => {
    setSelectedRowKeys([]);
    onDeleteSuccess?.();
  });

  const handleDeleteClick = () => {
    if (selectedRowKeys.length === 0) {
      toast.error('削除するデータを選択してください。');
      return;
    }
    openDeleteConfirm({
      okButtonProps: { loading: isDeleting },
      onOk: () => bulkDelete(selectedRowKeys),
    });
  };

  return (
    <AppContainer>
      <main className="space-y-5 px-4">
        <AppBreadcrumb items={breadcrumbItems} />
        <h3 className="text-xl">県別注意事項</h3>
        <AdminInsurerAttentionFilter onSearch={onSearch} />
        <AppButton
          type="primary"
          icon={<Plus />}
          onClick={() => navigate(`/${site}/admin/insurer_attentions/create`)}
        >
          新規
        </AppButton>
        <div ref={reflectBoxRef}>
          <AdminInsurerAttentionReflectBox
            record={reflectRecord}
            selectedRowKeys={selectedRowKeys}
            onBulkCopySuccess={onBulkCopySuccess}
          />
        </div>
        <div className="flex justify-end">
          <AppButton danger loading={isDeleting} onClick={handleDeleteClick}>
            一括削除
          </AppButton>
        </div>
        <div className="bg-white rounded-md border">
          <InsurerAttentionTable
            data={data}
            selectedRowKeys={selectedRowKeys}
            sortInfo={sortInfo}
            onTableChange={onTableChange}
            onSelectionChange={setSelectedRowKeys}
            onReflect={onReflect}
            isLoading={isLoading}
          />
          <div className="flex justify-end p-4">
            <AppPagination
              current={current}
              total={total}
              pageSize={pageSize}
              onChange={onChange}
            />
          </div>
        </div>
      </main>
    </AppContainer>
  );
};

export default AdminInsurerAttentionListTemplate;
